import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Users, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { setImpersonatedUser, getImpersonatedUser } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminUsersDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [currentImpersonation, setCurrentImpersonation] = useState(getImpersonatedUser());
  const qc = useQueryClient();

  const { data, refetch } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
    enabled: open && user?.role === "admin",
  });

  useEffect(() => {
    if (open) setCurrentImpersonation(getImpersonatedUser());
  }, [open]);

  if (user?.role !== "admin") return null;

  function viewAs(userId) {
    setImpersonatedUser(userId);
    setCurrentImpersonation(userId);
    qc.invalidateQueries();
    toast.success("Now viewing this user's data");
    setOpen(false);
  }

  function stopViewing() {
    setImpersonatedUser("");
    setCurrentImpersonation("");
    qc.invalidateQueries();
    toast.success("Back to your own data");
  }

  const users = data?.users || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-admin-users" className="flex items-center gap-2 px-3 py-2 w-full rounded-md text-sm text-slate-600 hover:bg-slate-100">
          <ShieldCheck className="w-4 h-4" /> Users {currentImpersonation && <span className="ml-auto text-xs text-amber-600 font-mono-data">viewing</span>}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> All Users</DialogTitle>
          <DialogDescription>
            Master admin view of every account. Click "View as" to browse their data as if you were them.
          </DialogDescription>
        </DialogHeader>

        {currentImpersonation && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm flex items-center justify-between">
            <span className="text-amber-900">You're viewing another user's data.</span>
            <button onClick={stopViewing} data-testid="stop-impersonation" className="inline-flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-md">
              <EyeOff className="w-3.5 h-3.5" /> Return to my data
            </button>
          </div>
        )}

        <div className="border border-[#E2E2E8] rounded-md overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 border-b border-[#E2E2E8] text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-3">Last Active</div>
            <div className="col-span-1 text-right">Entries</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {users.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">No other users yet.</div>
          ) : (
            users.map((u) => {
              const isSelf = u.id === user.id;
              const isViewing = currentImpersonation === u.id;
              return (
                <div key={u.id} data-testid={`user-row-${u.email}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#E2E2E8] last:border-b-0 hover:bg-slate-50">
                  <div className="col-span-4">
                    <div className="text-sm font-medium truncate">{u.email}</div>
                    <div className="text-xs text-slate-500 truncate">{u.name}</div>
                  </div>
                  <div className="col-span-2 text-xs font-mono-data uppercase text-slate-600">{u.role}</div>
                  <div className="col-span-3 text-xs text-slate-600">{formatDate(u.last_active_at)}</div>
                  <div className="col-span-1 text-right font-mono-data text-sm">{u.entries}</div>
                  <div className="col-span-2 text-right">
                    {isSelf ? (
                      <span className="text-xs text-slate-400">you</span>
                    ) : isViewing ? (
                      <button onClick={stopViewing} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-md">
                        <EyeOff className="w-3.5 h-3.5" /> Viewing
                      </button>
                    ) : (
                      <button
                        onClick={() => viewAs(u.id)}
                        data-testid={`view-as-${u.email}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#E2E2E8] hover:bg-blue-50 hover:text-[#2563EB] rounded-md"
                      >
                        <Eye className="w-3.5 h-3.5" /> View as
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <button onClick={() => refetch()} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Refresh</button>
          <button onClick={() => setOpen(false)} className="px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
